import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormAlertModel } from "../../../api/types/alert";
import { SelectElementModelBE, SelectElementModelFE } from "../../../api/types/generic";
import { useFormContext } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import { FormAutocomplete, FormDatePicker, FormTextField } from "../../../common/form-fields";
import { useRecoilState } from "recoil";
import { schedulingAtom } from "../../../state/alertRecoil";
import { fromGenericBeToGenericFe, setFormNumberValue } from "../../../utils";
import { numberIntegerRegex } from "../../../constants";
import { commercialSegmentListAtom } from "../../../state/commercialSegmentsAtom";
import { useCommercialSegmentRepository } from "../../../api/repositories/CommercialSegmentRepository";
import { DealerTenantEntity } from "../../../common/components/DealerAndTenant";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { Location, LocationModelFE } from "../../../api/types/location";
import { useLocationGroupRepository } from "../../../api/repositories/LocationGroupRepository";
import { useLocationRepository } from "../../../api/repositories/LocationRepository";

type AlertFormProps = {
  readonly: boolean;
};

const RpdForm = ({ readonly }: AlertFormProps) => {
  const [commercialSegmentList, setCommercialSegmentList] = useRecoilState<
    SelectElementModelFE[]
  >(commercialSegmentListAtom);

  // Use Translation
  const { t } = useTranslation(["translation", "pricing"]);
  const [schedulingList] = useRecoilState(schedulingAtom);

  //USE STATE
  const [locationGroups, setLocationGroups] = useState<SelectElementModelFE[]>(
    []
  );
  const [dealerOptions, setDealerOptions] = useState([]);
  const [location, setLocation] = useState<SelectElementModelFE[]>([]);

  //USE HOOK CUSTOM
  const { getLocationGroups } = useLocationGroupRepository();
  const { getCommercialSegments } = useCommercialSegmentRepository();
  const { getLocationAllFiltered } = useLocationRepository();

  // Use Context
  const {
    dispatchGlobalData,
    globalData: { user }
  } = useContext(GlobalContext);

  //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
  const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:legalEntities.".concat(el.name))
    })
  );

  //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
  const optionsFiltered = legalEntityOptions
    .filter(function(el) {
      return el.code !== "all";
    });

  const everyLegalEntitiesWithoutAll = legalEntityOptions
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  //setto tutti i dealers options
  const dealerOption: SelectElementModelFE[] = user.dealers?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:dealers.".concat(el.name))
    })
  );

  const dealerFilteredOptions = dealerOption
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  //USE FORM CONTEXT
  const { register, setValue, watch, formState, resetField, clearErrors } =
    useFormContext<FormAlertModel>();
  const watchAllForm = watch();

  const handleChangeMinRpd = (value: string) => {
    setFormNumberValue(value, setValue, "rpd.minRPD", numberIntegerRegex);
    if (numberIntegerRegex.test(value.trim())) {
      const rule = `${t(
        "pricing:alert.labels.rules.rpd.firstPartRule1"
      )}${Number(value)}`;
      setValue("rpd.firstRule", rule);
    }
  };

  const onChangeLocation = (input: SelectElementModelFE[]) => {
    const value: SelectElementModelFE[] =
      // è stato cliccato "selectAll
      input.find((el: SelectElementModelFE) => el.code === 0) &&
      // tra i vecchi valori non c'è "selectAll
      !watchAllForm.locations?.find((el: SelectElementModelFE) => el.code === 0)
        ? // applica il "selectAll
        location.filter((el: SelectElementModelFE) => el.code !== 0)
        : // setta solo i car group selezionati
        input.filter((el) => el.code !== 0);
    setValue("locations", value);
    clearErrors(["locationGroups", "locations"]);
  };

  const onChangeLocationGroup = (input: SelectElementModelFE[]) => {
    const value: SelectElementModelFE[] =
      // è stato cliccato "selectAll
      input.find((el: SelectElementModelFE) => el.code === 0) &&
      // tra i vecchi valori non c'è "selectAll
      !watchAllForm.locationGroups?.find(
        (el: SelectElementModelFE) => el.code === 0
      )
        ? // applica il "selectAll
        locationGroups.filter((el: SelectElementModelFE) => el.code !== 0)
        : // setta solo i car group selezionati
        input.filter((el) => el.code !== 0);
    setValue("locationGroups", value);
    clearErrors(["locationGroups", "locations"]);
  };

  const getStaticContent = async () => {
    const commercialSegmentsData = await getCommercialSegments();
    const commercialSegmentList: SelectElementModelFE[] =
      fromGenericBeToGenericFe(commercialSegmentsData);
    setCommercialSegmentList(commercialSegmentList);
  };

  useEffect(() => {
    getStaticContent();
    if (optionsFiltered.length === 1) {
      updateDealerSelection(optionsFiltered[0]);
    }
  }, []);

  const updateDealerSelection = async (value: SelectElementModelFE) => {
    setValue(`legalEntity`, value);
    setValue(`dealer`, null);
    const filteredOptionsDealer = user.dealers?.filter(
      (el: DealerTenantEntity) => el.legalEntities?.some((el: { name: string | number; }) => el.name === value?.code)
    );
    const filteredList = filteredOptionsDealer.map(
      (el: DealerTenantEntity) => ({
        code: el.name,
        label: t("translation:dealers.".concat(el.name))
      })
    );
    await setDealerOptions(filteredList);
    if (filteredList.length === 1) {
      setValue(`dealer`, filteredList[0]);
    }

    const location = await getLocationAllFiltered(value?.code as string);
    const locationsFe: LocationModelFE[] = fromGenericBeToGenericFe<
      Location,
      LocationModelFE
    >(location as Location[]).sort(
      (a: LocationModelFE, b: LocationModelFE) =>
        a.label.localeCompare(b.label)
    );
    setLocation([
      { code: 0, label: t("pricing:actions.selectAll") },
      ...locationsFe
    ]);

    const locationGroupsData: SelectElementModelBE[] =
      await getLocationGroups(value?.code as string);
    const locationsGroups: SelectElementModelFE[] =
      fromGenericBeToGenericFe(locationGroupsData);
    setLocationGroups([
      { code: 0, label: t("pricing:actions.selectAll") },
      ...locationsGroups
    ]);
  };

  const renderDealerAndTenant = () => {
    return (
      <>
        <Typography variant="h6" width={"100%"} pl={2} mt={4}
                    hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length <= 1}>
          {t("pricing:alert.labels.dealerAndTenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length > 1}>
          {t("pricing:alert.labels.tenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={dealerFilteredOptions.length <= 1 || optionsFiltered.length > 1 }>
          {t("pricing:alert.labels.dealer")}
        </Typography>
        <Grid item xs={12} md={3} hidden={optionsFiltered.length <= 1}>
          <FormAutocomplete
            {...register("legalEntity")}
            label={`${t("pricing:dealer.tenant")}*`}
            //getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.label))}
            options={optionsFiltered || []}
            handleChange={updateDealerSelection}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
        <Grid item xs={12} md={3} hidden={dealerFilteredOptions.length <= 1}>
          <FormAutocomplete
            {...register(`dealer`)}
            label={`${t("pricing:dealer.dealer")}*`}
            options={dealerOptions || []}
            readOnly={Boolean(!watch("legalEntity"))}
            //getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.label))}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
      </>
    );
  };

  return (
    <Grid container columnSpacing={2}>
      {/* DESCRIPTION/NAME */}
      <Typography variant="h6" width={"100%"} pl={2} mt={4}>
        {t("pricing:alert.labels.descriptionTitle")}
      </Typography>
      <Grid item xs={12} md={3}>
        <FormTextField
          {...register("description")}
          label={`${t("pricing:alert.labels.description")}*`}
          readOnly={readonly}
        />
      </Grid>
      {/* DEALER/TENANT */}
      {renderDealerAndTenant()}
      {/* LOCATIONS */}
      <Typography variant="h6" width={"100%"} pl={2} mt={4}>
        {t("pricing:alert.labels.searchParameters")}
      </Typography>
      <Grid item xs={12} md={3}>
        <FormAutocomplete
          multiple={true}
          {...register("locations")}
          label={`${t("pricing:alert.labels.locations")}*`}
          options={location}
          isOptionEqualToValue={(
            option: SelectElementModelFE,
            value: SelectElementModelFE
          ) => option?.code === value?.code}
          readOnly={readonly || watch("locationGroups")?.length > 0}
          handleChange={onChangeLocation}
        />
      </Grid>
      {/* GROUPS OF LOCATIONS */}
      <Grid item xs={12} md={3}>
        <FormAutocomplete
          multiple={true}
          {...register("locationGroups")}
          label={`${t("pricing:alert.labels.groupsOfLocations")}*`}
          options={locationGroups}
          isOptionEqualToValue={(
            option: SelectElementModelFE,
            value: SelectElementModelFE
          ) => option?.code === value?.code}
          readOnly={readonly || watch("locations")?.length > 0}
          handleChange={onChangeLocationGroup}
        />
      </Grid>
      {/* PICKUP DATE FROM */}
      <Grid item xs={12} md={3}>
        <FormDatePicker
          {...register("rpd.pickupDateFrom")}
          label={`${t("pricing:alert.labels.pickupDateFrom")}*`}
          readOnly={readonly}
        />
      </Grid>
      {/* PICKUP DATE TO */}
      <Grid item xs={12} md={3}>
        <FormDatePicker
          {...register("rpd.pickupDateTo")}
          label={`${t("pricing:alert.labels.pickupDateTo")}*`}
          readOnly={readonly}
        />
      </Grid>
      {/* COMMERCIAL SEGMENT */}
      <Grid item xs={12} md={3}>
        <FormAutocomplete
          {...register("rpd.commercialSegment")}
          label={`${t("pricing:report.form.smCommercialSegment")}`}
          options={commercialSegmentList}
          isOptionEqualToValue={(
            option: SelectElementModelFE,
            value: SelectElementModelFE
          ) => option.code === value.code}
          readOnly={readonly}
        />
      </Grid>
      {/* MIN RPD */}
      <Typography variant="h6" width={"100%"} pl={2} mt={4}>
        {t("pricing:alert.labels.thresholdParameters")}
      </Typography>
      <Grid item xs={12} md={3}>
        <FormTextField
          {...register("rpd.minRPD")}
          label={`${t("pricing:alert.labels.minRPD")}*`}
          type={"text"}
          handleChange={handleChangeMinRpd}
          readOnly={readonly}
        />
      </Grid>
      {/* REGOLA NUMERO 1 */}
      <Typography variant="h6" width={"100%"} pl={2} mt={4}>
        {t("pricing:alert.labels.rules.label")}
      </Typography>
      <Grid item xs={12} md={3}>
        <FormTextField {...register("rpd.firstRule")} readOnly={true} />
      </Grid>
      {/* DESTINATARI/EMAIL */}
      <Typography variant="h6" width={"100%"} pl={2} mt={4}>
        {t("pricing:alert.labels.receiverTitle")}
      </Typography>
      <Grid item xs={12} md={6}>
        <FormAutocomplete
          options={[]}
          freeSolo={true}
          multiple={true}
          {...register("recipientLists")}
          label={`${t("pricing:alert.labels.receiver")}*`}
          helperText={
            formState.errors.recipientLists
              ? formState.errors.recipientLists[0]?.message
              : ""
          }
          readOnly={readonly}
        />
      </Grid>
      {/* SCHEDULAZIONE */}
      <Typography variant="h6" width={"100%"} pl={2} mt={4}>
        {t("pricing:alert.labels.scheduling.label")}
      </Typography>
      <Grid item xs={12} md={3}>
        <FormAutocomplete
          {...register("schedulingType")}
          label={`${t("pricing:alert.labels.scheduling.placeholder")}*`}
          options={schedulingList}
          isOptionEqualToValue={(
            option: SelectElementModelFE,
            value: SelectElementModelFE
          ) => option.code === value.code}
          handleChange={(value: SelectElementModelFE) => {
            resetField("crons");
            setValue("schedulingType", value);
            if (
              watch("schedulingType")?.label ===
              t("pricing:alert.labels.scheduling.daily")
            ) {
              setValue(
                "crons.radioType",
                t("pricing:alert.labels.everyWeekDay")
              );
            } else {
              setValue("crons.radioType", t("pricing:alert.labels.every"));
            }
          }}
          readOnly={readonly}
        />
      </Grid>
    </Grid>
  );
};

export default RpdForm;
